import { useMemo, useState, type ReactNode } from 'react';
import { useLeafState, useMeta } from '@customerio/dryad-react';
import { useObjectAttributeValues } from '@/services/db/attribute/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { AttributeRow } from '../components/attribute-row/attribute-row';
import { groupsForFamilies, type GroupableFamily } from '../operator-groups';
import { ObjectAttributeNamePicker, useObjectAttributeNames } from './object-attribute-name-picker';

/** The editing state an object-attribute leaf holds, whichever wire stores it. */
type ObjectAttributeRowState = {
  field: string;
  operator: string;
  value: string | null;
  value2: string | null;
  typeId: number | string | null;
};

export type ObjectAttributeRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's object-attribute leaf. */
  conditionType: string;
  /** The operator families this surface offers, which the dropdown derives from. */
  families: readonly GroupableFamily[];
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
  /**
   * The object type whose attributes are offered, where the surface knows it
   * independently of the row. Falls back to the type the condition itself
   * carries, which is what a stored condition read off the wire has.
   */
  objectTypeId?: number | string | null;
  /** Names the surface already holds, offered before any search lands. */
  localAttributeNames?: readonly string[];
};

const NO_LOCAL_NAMES: readonly string[] = [];

/**
 * An object-attribute condition row: [type] [attribute name] [operator] [value…].
 *
 * Shared by the object list's filters and the object trigger's conditions,
 * which edit the same condition on two different wires — a proto envelope and a
 * flat array. What they have in common is everything the reader sees: the same
 * attribute catalogue scoped to one object type, the same value suggestions off
 * that type, the same operators. What differs is the encoding, which the
 * surface's plugin owns, and the operators offered, which arrive as a prop.
 */
export function ObjectAttributeRow({
  id,
  conditionType,
  families,
  typeSelect,
  objectTypeId,
  localAttributeNames = NO_LOCAL_NAMES,
}: ObjectAttributeRowProps) {
  const workspaceId = requireWorkspaceId();
  const meta = useMeta();
  const { state } = useLeafState<ObjectAttributeRowState>(id, conditionType);

  // The name picker's "Select an attribute" renders only once the picker has
  // been visited — or from the start when the row mounted with a name already
  // chosen — so a freshly added row does not open covered in red.
  const [isFieldTouched, setFieldTouched] = useState(() => state.field !== '');

  // The surface's own object type wins where it has one: a fresh condition
  // carries none until the surface's seed stamps it, and the attribute
  // catalogue should still be scoped while that is being picked.
  const scopedTypeId = String(objectTypeId ?? state.typeId ?? '');
  const names = useObjectAttributeNames(scopedTypeId, state.field, localAttributeNames);
  const values = useObjectAttributeValues(workspaceId, scopedTypeId, state.field);
  const valueOptions = useMemo(() => (values.data ?? []).map((value) => ({ value, label: value })), [values.data]);

  return (
    <AttributeRow
      id={id}
      conditionType={conditionType}
      operatorGroups={(field) => groupsForFamilies(families, field)}
      isDisabled={meta.isDisabled}
      valueOptions={valueOptions}
      isLoadingValueOptions={values.isFetching}
      isFieldTouched={isFieldTouched}
      onFieldTouched={() => setFieldTouched(true)}
      typeSelect={typeSelect}
      renderNamePicker={(pickerProps) => <ObjectAttributeNamePicker {...pickerProps} names={names} />}
    />
  );
}
